'use client'
import type {
    ComponentProps,
    ComponentPropsWithRef,
    ElementType,
    HTMLAttributes,
    PropsWithChildren,
    ReactNode
} from 'react'
import { forwardRef } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { cva, cx } from 'class-variance-authority'
import { X } from 'lucide-react'
import Icon from '../Icon'
import { isNil } from 'lodash'

export const DrawerClose = DialogPrimitive.Close
export const DrawerTrigger = DialogPrimitive.Trigger
export const DrawerTitle = DialogPrimitive.Title

export interface DrawerProps extends ComponentProps<typeof DialogPrimitive.Root> {}

export const DrawerRoot = ({ children, ...props }: DrawerProps) => {
    return <DialogPrimitive.Root {...props}>{children}</DialogPrimitive.Root>
}

export interface DialogContentProps extends PropsWithChildren<HTMLAttributes<HTMLDivElement>> {
    /**
     * Size of the drawer
     * @default 1 (500px)
     */
    size?: 1 | 2 | 3 | 4 | 5
    /**
     * Title of the drawer
     */
    title?: string
    /**
     * Actions to add at the top
     */
    actions?: ReactNode
    /**
     * Children of the drawer
     */
    children: ReactNode
    /**
     * Footer content of the drawer
     */
    footer?: ReactNode
}

const overlayStyles = cva(
    'h-full fixed top-0 left-0 w-full bg-overlay overflow-y-auto data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in',
    {
        variants: {
            state: {
                open: 'animate-fade-in',
                closed: 'animate-fade-out'
            }
        }
    }
)

const contentStyles = cva(
    'fixed top-5 bottom-5 right-5 p-8 flex flex-col gap-6 rounded-4 bg-backgroundPrimary scrollbar-none data-[state=closed]:animate-to-right data-[state=open]:animate-from-right',
    {
        variants: {
            size: {
                1: 'w-125',
                2: 'w-[clamp(500px,800px,calc(100vw-10%))]',
                3: 'w-[clamp(500px,1000px,calc(100vw-10%))]',
                4: 'w-[clamp(500px,1200px,calc(100vw-10%))]',
                5: 'w-[clamp(500px,1400px,calc(100vw-10%))]'
            }
        },
        defaultVariants: {
            size: 1
        }
    }
)

export const DrawerContent = forwardRef<HTMLDivElement, DialogContentProps>(
    ({ children, size = 1, ...props }, forwardedRef) => {
        return (
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className={overlayStyles()}>
                    <DialogPrimitive.Content
                        onClick={(e) => e.stopPropagation()}
                        data-testid="__drawer__"
                        ref={forwardedRef}
                        className={contentStyles({ size })}
                        aria-describedby="drawer-description"
                        {...props}
                    >
                        {children}
                    </DialogPrimitive.Content>
                </DialogPrimitive.Overlay>
            </DialogPrimitive.Portal>
        )
    }
)

export const DrawerHeader = ({ children, actionButton }: { children: ReactNode; actionButton?: ReactNode }) => {
    return (
        <div className="flex justify-between items-center gap-0 p-1">
            <div className="flex gap-0">
                <DrawerTitle>{children}</DrawerTitle>
            </div>
            <div className="flex items-center gap-2">
                {!isNil(actionButton) ? actionButton : null}
                <DrawerClose asChild data-testid="__drawer-close-button__">
                    <button type="button" className="border-0.5 border-borderPrimary rounded-sm p-2">
                        <Icon icon={X} />
                    </button>
                </DrawerClose>
            </div>
        </div>
    )
}

export const DrawerBody = ({ children }: { children: ReactNode }) => {
    return <div className="w-full scrollbar-none overflow-y-scroll p-1 flex flex-col gap-6">{children}</div>
}

export const DrawerFooter = ({ children }: { children: ReactNode }) => {
    return <div className="flex items-center w-full p-1">{children}</div>
}

interface DrawerContainerProps<T extends ElementType = 'div'> {
    as?: T
    children: ReactNode
    className?: string
}

type Props<T extends ElementType> = DrawerContainerProps<T> &
    Omit<ComponentPropsWithRef<T>, keyof DrawerContainerProps<T>>

export const DrawerContainer = forwardRef<HTMLElement, Props<ElementType>>(
    ({ as, children, className, ...props }, ref) => {
        const Component = as || 'div'
        return (
            <Component
                ref={ref}
                className={cx([
                    'max-h-full min-h-full grid gap-4 grid-rows-[min-content_minmax(0,1fr)_min-content]',
                    className
                ])}
                {...props}
            >
                <VisuallyHidden>
                    <DialogPrimitive.DialogDescription />
                </VisuallyHidden>
                {children}
            </Component>
        )
    }
)
